import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { useAdminStore } from '../../stores/adminStore';
import { Table, TableBody, TableCell, TableHeader, TableRow } from '../table';
import { Button } from '../common/Button';
import { AdminReviewType } from '../../types/review';
import { useLocation } from 'react-router-dom';
import ReviewModalActions from './ReviewModalActions';

const AdminReviewList: React.FC = () => {
  const { t } = useTranslation(['common', 'admin', 'reviews', 'common']);
  const { reviews, fetchReviews, fetchMoreReviews, hasMore, updateReviewStatus } = useAdminStore();

  const [loading, setLoading] = useState(false);

  const location = useLocation();
  const entityId = location.state;

  const [showCancelReviewModal, setShowCancelReviewModal] = useState(false);
  const [selectedReview, setSelectedReview] = useState<AdminReviewType | null>(null);

  useEffect(() => {
    if (!reviews.length) {
      setLoading(true);
      fetchReviews()
        .catch(() => {
          toast.error(t('generic_error', { ns: 'common' }));
        })
        .finally(() => setLoading(false));
    }
  }, [reviews.length, fetchReviews, t]);

  useEffect(() => {
    if (entityId) {
      const foundReview = reviews.find(review => review.id === entityId);
      if (foundReview) {
        setSelectedReview(foundReview);
        setShowCancelReviewModal(true);
      }
    }
  }, [entityId, reviews]);

  const handleLoadMore = async () => {
    setLoading(true);
    try {
      await fetchMoreReviews();
    } catch {
      toast.error(t('generic_error', { ns: 'common' }));
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateReviewStatus = async (review: AdminReviewType, validated: boolean) => {
    try {
      await updateReviewStatus(review.id, validated);
      setShowCancelReviewModal(false);
      setSelectedReview(null);
    } catch (error) {
      console.error('Error updating review status:', error);
      toast.error(t('generic_error', { ns: 'common' }));
    }
  };

  return (
    <>
      <div className="w-full pt-4 pb-5 flex flex-col md:w-2/3 md:px-1 md:pr-8 md:pb-8">
        <h1 className="text-5xl font-bold hyphens-auto">
          {t('navigation.reviews', { ns: 'admin' })}
        </h1>
      </div>
      {reviews.length > 0 ? (
        <div className="overflow-hidden pb-4 rounded-2xl bg-white border border-gray-300 shadow-sm transition-all">
          <div className="max-w-full overflow-x-auto">
            <Table className="w-full">
              <TableHeader className="bg-gray-50">
                <TableRow>
                  {['reviewer', 'reviewed', 'rating', 'created_at'].map(field => (
                    <TableCell
                      key={field}
                      isHeader
                      className="text-start px-6 py-4 font-semibold uppercase text-gray-600 text-[0.75rem] tracking-wide"
                    >
                      {t(`table_fields.${field}`, { ns: 'admin' })}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHeader>

              <TableBody className="divide-y divide-gray-100">
                {reviews.map(review => (
                  <TableRow
                    key={review.id}
                    className="hover:bg-gray-50 cursor-pointer"
                    onClick={() => {
                      setSelectedReview(review);
                      setShowCancelReviewModal(true);
                    }}
                  >
                    <TableCell className="px-6 py-4">{review.reviewer.first_name}</TableCell>
                    <TableCell className="px-6 py-4">{review.reviewed.first_name}</TableCell>
                    <TableCell className="px-6 py-4">{review.rating}/5</TableCell>
                    <TableCell className="px-6 py-4 text-gray-600 text-sm">
                      {new Date(review.created_at).toLocaleDateString()}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>

          {hasMore?.reviews && (
            <div>
              <Button
                className="w-full my-4 md:w-auto md:px-8 md:justify-self-center"
                onClick={() => handleLoadMore()}
                disabled={loading}
              >
                {loading
                  ? t('loading', { defaultValue: 'Chargement...', ns: 'common' })
                  : t('loadMore', { defaultValue: 'Charger plus', ns: 'common' })}
              </Button>
            </div>
          )}
        </div>
      ) : (
        <div className="flex flex-col items-center p-6 text-center">
          {t('nothing_to_show', { ns: 'common' })}
        </div>
      )}
      {showCancelReviewModal && selectedReview && (
        <ReviewModalActions
          review={selectedReview}
          onClose={() => setShowCancelReviewModal(false)}
          onApprove={review => handleUpdateReviewStatus(review, true)}
          onReject={review => handleUpdateReviewStatus(review, false)}
        />
      )}
    </>
  );
};

export default AdminReviewList;
